import styled from 'styled-components'
import { Fade } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SubmitButton from 'components/form/SubmitButton'
import { useSelector } from 'react-redux'
import { selectCourse } from 'store/course/slice'
import { useIntl } from 'react-intl'

interface Props {
  handleSubmit: () => void
  close: () => void
}

export default function ConfirmCourseVersionSaveDialog({ handleSubmit, close }: Props): JSX.Element {
  const intl = useIntl()
  const { versionSaveStatus, versionZendeskStatus } = useSelector(selectCourse)

  const getStatus = () => {
    return versionSaveStatus !== 'succeeded' ? versionSaveStatus : versionZendeskStatus
  }

  return (
    <>
      <Fade in unmountOnExit>
        <Background>
          <Container>
            <TopBar>
              <Close onClick={close} />
            </TopBar>

            <Content>
              <Title>{intl.formatMessage({ id: 'courseEditor.sendDataDialogTitle' })}</Title>
              <Text>{intl.formatMessage({ id: 'courseEditor.sendDataDialogText' })}</Text>
              <ButtonsContainer>
                <SubmitData
                  status={getStatus()}
                  text={intl.formatMessage({ id: 'courseEditor.sendCourseData' })}
                  handleSubmit={handleSubmit}
                />
                <ContinueEditing disabled={getStatus() === 'loading'} onClick={close}>
                  {intl.formatMessage({ id: 'strings.continueEditing' })}
                </ContinueEditing>
              </ButtonsContainer>
            </Content>
          </Container>
        </Background>
      </Fade>
    </>
  )
}

const Background = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) =>
    props.theme.palette.type === 'light' ? 'rgba(255,255,255,0.8)' : 'rgba(68,68,68,0.8)'};
  z-index: 10000;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  box-shadow: 0px 14px 24px 0px #0000001a;
  border-radius: 8px;
  width: 500px;
  height: 315px;
`

const TopBar = styled.div`
  height: 44px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  padding: 20px 40px;
`

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  line-height: 26px;
  color: #032615;
`

const Text = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 27px;
  color: #032615;
  padding-bottom: 20px;
`

const Close = styled(CloseIcon)`
  float: right;
  padding: 10px;
  font-weight: 700;
  cursor: pointer;
`

const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  height: 59px;
  gap: 20px;
  padding-bottom: 20px;
`

const SubmitData = styled(SubmitButton)`
  width: 50%;
  border-radius: 8px;
`

const ContinueEditing = styled.button`
  width: 50%;
  height: 100%;
  background-color: transparent;
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  text-align: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
